import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const CardContainer = styled.div`

`;

const CardContent = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 20px auto;
  background: #E7E6E4;
  opacity: ${props => props.$available ? 1 : 0.5};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  overflow: hidden;
  min-height: 200px;

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
  }
  opacity: ${props => props.$available ? 1 : 0.5};

  @media (min-width: 769px) {
    position: relative;
    transition: transform 0.3s ease-in-out;
    &:hover {
      transform: scale(1.01);
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.1);
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
      pointer-events: none;
    }

    &:hover::after {
      opacity: 1;
    }
  }

`;

const InfoSection = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RoomTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.3rem;
  color: #333;
`;

const RoomName = styled.h2`
  margin: 0 0 10px 0;
  font-size: 1.5rem;
`;

const NewRentStartDate = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #222;
`;

const Status = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${props => props.$available ? 'green' : 'red'};
`;

const BaseRent = styled.p`
  margin: 10px 0;
  font-size: 1.0rem;
  color: #333;
`;

const YouTubeLink = styled.a`
  display: inline-block;
  margin: 10px 0;
  font-size: 1.0rem;
  color: #61916e;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #45a049;
    text-decoration: underline;
  }
`;

const ImagesGrid = styled.div`
  flex: 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImageThumbnail = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;

  &:hover {
    transform: scale(1.03);
  }

  border-radius: 8px;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.3);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-size: 24px;
  transition: background-color 0.3s;
  z-index: 1001;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }

  ${props => props.$position === 'left' ? 'left: 10px;' : 'right: 10px;'}
`;

const PopupImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  touch-action: none;
`;

function RoomCard({ roomName, roomTitle, status, images, baseRent, newRentStartDate, youtubeUrl}) {
  const [popupImage, setPopupImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleImageClick = (image) => {
    const index = images.findIndex(img => img.src === image.src);
    setCurrentImageIndex(index);
    setPopupImage(image);
  };

  const closePopup = () => {
    setPopupImage(null);
    setCurrentImageIndex(0);
  };

  const navigateImage = (direction) => {
    const newIndex = (currentImageIndex + direction + images.length) % images.length;
    setCurrentImageIndex(newIndex);
    setPopupImage(images[newIndex]);
  };

  // Mobile touch handlers
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swiped left
        navigateImage(1);
      } else {
        // Swiped right
        navigateImage(-1);
      }
    }
  };

  const isAvailable = status;

  return (
    <CardContainer $available={isAvailable}>
      <CardContent $available={isAvailable}>
        <InfoSection>
          <RoomName>{roomName}</RoomName>
          <RoomTitle>{roomTitle}</RoomTitle>
          <Status $available={isAvailable}>
            {isAvailable ? '歡迎約看' : '已出租'}
          </Status>
          <BaseRent>
            租金：{baseRent}
          </BaseRent>
          {newRentStartDate && (
            <NewRentStartDate>
              最快可入住日：{newRentStartDate}
            </NewRentStartDate>
          )}
          {youtubeUrl && (
            <YouTubeLink 
              href={youtubeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              📹 觀看影片
            </YouTubeLink>
          )}

        </InfoSection>
        <ImagesGrid>
          {images.slice(0, 20).map((image, index) => (
            <ImageThumbnail
              key={index}
              src={image.src}
              onClick={() => handleImageClick(image)}
            />
          ))}
        </ImagesGrid>
      </CardContent>
      {popupImage && (
        <PopupOverlay onClick={closePopup}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <NavigationButton
              $position="left"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(-1);
              }}
            >
              ‹
            </NavigationButton>
            <PopupImage
              src={popupImage.src}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
            <NavigationButton
              $position="right"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage(1);
              }}
            >
              ›
            </NavigationButton>
          </PopupContent>
        </PopupOverlay>
      )}
    </CardContainer>
  );
}

export default RoomCard;